import {
  BellIcon,
  Building2Icon,
  CalendarCheckIcon,
  ChartColumnIcon,
  CircleCheckIcon,
  CirclePlusIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  BoxesIcon,
  FileTextIcon,
  LayoutDashboardIcon,
  PackageCheckIcon,
  ReceiptTextIcon,
  SlidersHorizontalIcon,
  TagsIcon,
  TruckIcon,
  UsersIcon,
  type LucideIcon,
} from "lucide-react";
import type { Role } from "@shared/roles";

/**
 * Which screens each role sees.
 *
 * This drives navigation only. It is **not** the permission boundary — that
 * lives in convex/lib/authz.ts and is enforced on the server. Hiding a link is
 * a courtesy to the user, never a security control.
 */

/**
 * The headings the desktop sidebar files destinations under.
 *
 * They name the job, not the screen — an owner scanning for "why has this not
 * shipped" looks under Deliveries, not under a list of eleven nouns. An item
 * with no group sits above the first heading, which is where a role's landing
 * screen belongs.
 */
export type NavGroup = "Ordering" | "Deliveries" | "Stock" | "Billing" | "Admin";

/** Render order for the sidebar's headings. Independent of the per-role item
 *  order below, which the mobile bottom bar depends on. */
export const NAV_GROUPS: readonly NavGroup[] = [
  "Ordering",
  "Deliveries",
  "Stock",
  "Billing",
  "Admin",
];

export interface NavItem {
  to: string;
  label: string;
  /** Shown in the bottom bar on mobile. Only the first four fit at 390px. */
  primary: boolean;
  /** Sidebar heading on desktop. Ungrouped items render first, unheaded. */
  group?: NavGroup;
}

const NAV: Record<Role, NavItem[]> = {
  assistant: [
    // The weekly count is v2's primary loop, so it takes the first tab — but
    // the landing screen stays /request (see homeFor): the two-tap request
    // gate is measured from app-open and does not move.
    { to: "/count", label: "Count", primary: true, group: "Stock" },
    { to: "/request", label: "Request", primary: true, group: "Ordering" },
    { to: "/requests", label: "My clinic", primary: true, group: "Ordering" },
    { to: "/receive", label: "Receive", primary: true, group: "Deliveries" },
  ],
  manager: [
    { to: "/approvals", label: "Approvals", primary: true, group: "Ordering" },
    { to: "/request", label: "Request", primary: true, group: "Ordering" },
    { to: "/receive", label: "Receive", primary: true, group: "Deliveries" },
    { to: "/invoices", label: "Invoices", primary: true, group: "Billing" },
    { to: "/count", label: "Count", primary: false, group: "Stock" },
    { to: "/levels", label: "Stock levels", primary: false, group: "Stock" },
    { to: "/statements", label: "Statements", primary: false, group: "Billing" },
    { to: "/requests", label: "All requests", primary: false, group: "Ordering" },
  ],
  warehouse: [
    { to: "/inventory", label: "Inventory", primary: true, group: "Stock" },
    { to: "/dispatch", label: "Dispatch", primary: true, group: "Deliveries" },
    { to: "/requests", label: "Requests", primary: true, group: "Ordering" },
    { to: "/alerts", label: "Alerts", primary: true, group: "Stock" },
  ],
  owner: [
    { to: "/", label: "Overview", primary: true },
    { to: "/approvals", label: "Approvals", primary: true, group: "Ordering" },
    { to: "/reports", label: "Reports", primary: true },
    { to: "/invoices", label: "Invoices", primary: true, group: "Billing" },
    { to: "/dispatch", label: "Dispatch", primary: false, group: "Deliveries" },
    { to: "/inventory", label: "Inventory", primary: false, group: "Stock" },
    { to: "/statements", label: "Statements", primary: false, group: "Billing" },
    { to: "/pricing", label: "Pricing rules", primary: false, group: "Billing" },
    { to: "/compliance", label: "Counts", primary: false, group: "Stock" },
    { to: "/levels", label: "Stock levels", primary: false, group: "Stock" },
    { to: "/alerts", label: "Alerts", primary: false, group: "Stock" },
    { to: "/settings/clinics", label: "Clinics", primary: false, group: "Admin" },
    { to: "/settings/users", label: "Staff", primary: false, group: "Admin" },
  ],
};

export const navFor = (role: Role): NavItem[] => NAV[role];

/**
 * A glyph per destination, for the desktop sidebar's collapsed icon rail.
 *
 * Keyed by `to` rather than declared on each `NavItem`, because the same screen
 * appears under three different labels across roles — "/requests" is "My
 * clinic", "All requests" and "Requests" — and a destination that changed
 * picture depending on who was looking at it would be worse than no picture.
 * One route, one glyph, in every role.
 *
 * Decoration only: the labels are what a person reads and what the harness
 * matches on, and every icon sits beside its label whenever the rail is open.
 */
export const NAV_ICONS: Record<string, LucideIcon> = {
  "/": LayoutDashboardIcon,
  "/request": CirclePlusIcon,
  "/requests": ClipboardListIcon,
  "/count": ClipboardCheckIcon,
  "/compliance": CalendarCheckIcon,
  "/levels": SlidersHorizontalIcon,
  "/approvals": CircleCheckIcon,
  "/receive": PackageCheckIcon,
  "/dispatch": TruckIcon,
  "/inventory": BoxesIcon,
  "/alerts": BellIcon,
  "/invoices": ReceiptTextIcon,
  "/statements": FileTextIcon,
  "/pricing": TagsIcon,
  "/reports": ChartColumnIcon,
  "/settings/clinics": Building2Icon,
  "/settings/users": UsersIcon,
};

/**
 * The same destinations, filed under their headings, for the desktop sidebar.
 *
 * Derived rather than a second table: a destination added to `NAV` appears in
 * the sidebar automatically, and cannot be reachable on a phone but missing on
 * a desktop. Empty groups are dropped, so a role never sees a heading with
 * nothing under it.
 */
export const navGroups = (
  role: Role,
): Array<{ heading: NavGroup | null; items: NavItem[] }> => {
  const items = navFor(role);
  const sections: Array<{ heading: NavGroup | null; items: NavItem[] }> = [];

  const ungrouped = items.filter((item) => !item.group);
  if (ungrouped.length > 0) sections.push({ heading: null, items: ungrouped });

  for (const heading of NAV_GROUPS) {
    const inGroup = items.filter((item) => item.group === heading);
    if (inGroup.length > 0) sections.push({ heading, items: inGroup });
  }

  return sections;
};

/**
 * Where each role lands on open.
 *
 * An assistant lands on /request with the item palette already focused. That is
 * not a convenience — it is the first of the two taps, and routing them
 * anywhere else makes the two-click constraint arithmetically impossible.
 */
export const homeFor = (role: Role): string =>
  role === "assistant"
    ? "/request"
    : role === "warehouse"
      ? "/inventory"
      : role === "manager"
        ? "/approvals"
        : "/";
